import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CommonsUtils } from '../../commons/CommonsUtils';
import { InsuredDTO, InsuredRelationship, INSURED_RELATIONSHIP_OPTIONS, normalizeRelationship } from '../../header-footer-template/model/InsuredDTO';

@Component({
    selector: 'app-insured-modal',
    templateUrl: './insured-modal.component.html',
    styleUrls: ['./insured-modal.component.scss']
})
export class InsuredModalComponent implements OnChanges {

    @Input() show = false;
    // Cuando se informa, el modal precarga sus datos y pasa a modo modificación.
    @Input() insured: any = null;
    // Pólizas DJ29 seleccionables al crear un asegurado. En modificación no se usa (la póliza
    // del asegurado no cambia).
    @Input() policies: Array<{ sequence: number; label: string }> = [];
    @Output() closed = new EventEmitter<void>();
    @Output() saved = new EventEmitter<{ dto: InsuredDTO; policySequence: number }>();

    form: FormGroup;
    relationships = INSURED_RELATIONSHIP_OPTIONS;
    prefixes = CommonsUtils.phonePrefix();
    submitted = false;
    running = false;

    /** Prefijo preseleccionado al dar de alta, y el que se asume si Innova no devuelve ninguno. */
    private static readonly DEFAULT_PREFIX = '34';

    /**
     * Con prefijo español se exigen los nueve dígitos de siempre; con cualquier otro no se
     * conoce la longitud del número, así que basta con que sean dígitos. Es el mismo criterio
     * que aplica el backend en InsuredSupplementValidationCoreCmd.
     */
    private static phoneValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) { return null; }

        const prefixControl = control.parent ? control.parent.get('phonePrefix') : null;
        const prefix = prefixControl ? prefixControl.value : InsuredModalComponent.DEFAULT_PREFIX;

        const pattern = (prefix === InsuredModalComponent.DEFAULT_PREFIX)
            ? /^[0-9]{9}$/
            : /^[0-9]{4,15}$/;

        return pattern.test(value) ? null : { pattern: true };
    }

    /**
     * Solo pueden asegurarse mayores de edad, así que se rechaza una fecha de nacimiento de
     * la que se deduzcan menos de 18 años. Se deja pasar lo que no le corresponde (vacío,
     * formato incorrecto o día inexistente): de eso ya avisan required y pattern.
     */
    private static adultValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value || !/^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/.test(value)) {
            return null;
        }

        const parts = value.split('/');
        const day = Number(parts[0]);
        const month = Number(parts[1]);
        const year = Number(parts[2]);

        const birthDate = new Date(year, month - 1, day);
        if (birthDate.getFullYear() !== year || birthDate.getMonth() !== (month - 1) || birthDate.getDate() !== day) {
            return null;
        }

        // Es mayor de edad el mismo día que cumple los 18.
        const adultFrom = new Date(year + 18, month - 1, day);
        return (adultFrom > new Date()) ? { underage: true } : null;
    }

    get isEditMode(): boolean {
        return !!this.insured;
    }

    /** Un número español son 9 dígitos; con otro prefijo se deja margen para longitudes mayores. */
    get phoneMaxLength(): number {
        return this.isSpanishPrefix ? 9 : 15;
    }

    get phoneErrorDescription(): string {
        return this.isSpanishPrefix ? '(*) 9 dígitos sin espacios' : '(*) Solo dígitos, sin espacios';
    }

    private get isSpanishPrefix(): boolean {
        return this.form.get('phonePrefix').value === InsuredModalComponent.DEFAULT_PREFIX;
    }

    // La póliza no es editable en modificación ni cuando el usuario solo tiene una póliza DJ29
    // (se autorrellena); solo se muestra como combo al crear con varias pólizas.
    get isPolicyFixed(): boolean {
        return this.isEditMode || (this.policies && this.policies.length === 1);
    }

    get selectedPolicyLabel(): string {
        const seq = this.form.get('policySequence').value;
        const policy = (this.policies || []).find(p => String(p.sequence) === String(seq));
        return policy ? policy.label : (seq !== null && seq !== undefined && seq !== '' ? String(seq) : '');
    }

    private resolveInitialPolicy(): any {
        if (this.isEditMode) {
            return this.insured ? this.insured.policySequence : '';
        }
        return (this.policies && this.policies.length === 1) ? this.policies[0].sequence : '';
    }

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({
            policySequence: ['', [Validators.required]],
            name:           ['', [Validators.required]],
            firstSurname:   ['', [Validators.required]],
            lastName:       [''],
            documentNumber: ['', [Validators.required]],
            phonePrefix:    [InsuredModalComponent.DEFAULT_PREFIX, [Validators.required]],
            phone:          ['', [Validators.required, InsuredModalComponent.phoneValidator]],
            relationship:   ['', [Validators.required]],
            birthDate:      ['', [Validators.required, Validators.pattern('^[0-9]{2}/[0-9]{2}/[0-9]{4}$'),
                                  InsuredModalComponent.adultValidator]],
            email:          ['', [Validators.required, Validators.pattern(CommonsUtils.EMAIL_REGEX)]]
        });

        // La regla que se aplica al número depende del prefijo elegido, así que hay que
        // revalidarlo al cambiarlo; si no, el error se quedaría con el criterio del anterior.
        this.form.get('phonePrefix').valueChanges.subscribe(() => {
            this.form.get('phone').updateValueAndValidity();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.show && changes.show.currentValue === true) {
            // El listado de Innova ya devuelve la relación de parentesco (TITURELA), así que
            // en modo modificación ese campo se precarga como el resto.
            this.form.reset({
                policySequence: this.resolveInitialPolicy(),
                name:           this.insured ? (this.insured.name || '') : '',
                firstSurname:   this.insured ? (this.insured.firstSurname || '') : '',
                lastName:       this.insured ? (this.insured.lastName || '') : '',
                documentNumber: this.insured ? (this.insured.documentNumber || '') : '',
                phonePrefix:    (this.insured && this.insured.mobilePhonePrefix)
                                    ? this.insured.mobilePhonePrefix
                                    : InsuredModalComponent.DEFAULT_PREFIX,
                phone:          this.insured ? (this.insured.mobilePhone || '') : '',
                relationship:   this.insured ? normalizeRelationship(this.insured.relationship) : '',
                birthDate:      this.insured ? (this.insured.bornDate || '') : '',
                email:          this.insured ? (this.insured.email || '') : ''
            });
            this.submitted = false;
            this.running = false;
        }
    }

    isInvalid(name: string): boolean {
        const c = this.form.get(name);
        return !!(c && c.invalid && (c.dirty || c.touched || this.submitted));
    }

    close(): void {
        if (this.running) { return; }
        this.closed.emit();
    }

    save(): void {
        this.submitted = true;
        if (this.form.invalid || this.running) { return; }
        this.running = true;
        const value = this.form.value;
        const dto: InsuredDTO = {
            name:           value.name,
            firstSurname:   value.firstSurname,
            lastName:       value.lastName || '',
            documentNumber: value.documentNumber,
            phone:          value.phone,
            phonePrefix:    value.phonePrefix,
            relationship:   value.relationship as InsuredRelationship,
            bornDate:       this.toIsoDate(value.birthDate),
            email:          value.email || ''
        };
        this.saved.emit({ dto, policySequence: Number(value.policySequence) });
    }

    /** Llamar desde el padre tras éxito/error para liberar el estado de envío. */
    setRunning(running: boolean): void {
        this.running = running;
    }

    // El backend espera la fecha en formato ISO (yyyy-MM-dd) para InsuredSupplementDTO.bornDate;
    // el formulario la captura como dd/mm/yyyy.
    private toIsoDate(ddMmYyyy: string): string {
        const [day, month, year] = ddMmYyyy.split('/');
        return `${year}-${month}-${day}`;
    }
}
